import { MAX_CHARACTER_FULLNAME_VALIDATION, MAX_CHARACTER_INPUT_VALIDATION, MAX_TEXT_INPUT_VALIDATION } from "@/constants/common";
import { validator } from "@/utils/validator";
import { dayjs } from "@/utils/dayjs";
import { replaceStr } from "@/utils/string";
import { INVALID_SELECT_NOT_EMPTY } from "@/constants/invalids";
import { ValidationError } from "yup";

const FIVE_YEARS_IN_MONTHS = 5 * 12;
const MAX_EXAM_SCORE = 1000;

const checkRemainingTerm = (
  clause1StartDate: unknown,
  blankPeriodMonths: unknown,
  path: string,
  createError: (params: { path: string; message: string }) => ValidationError,
): true | ValidationError => {
  if (!clause1StartDate) return true;

  const blankPeriod = Number(blankPeriodMonths) || 0;
  const elapsedMonths = dayjs(new Date()).diff(dayjs(clause1StartDate as string), "M");
  const remainingMonths = FIVE_YEARS_IN_MONTHS - elapsedMonths + blankPeriod;

  if (remainingMonths < 0) {
    return createError({
      path,
      message: "「1号の起算日」と「ブランク期間（月単位）」の組み合わせにより、1号残期間（自動計算）が0を下回っています。",
    });
  }

  if (remainingMonths > FIVE_YEARS_IN_MONTHS) {
    return createError({
      path,
      message: "「1号の起算日」と「ブランク期間（月単位）」の組み合わせにより、1号残期間（自動計算）が5年0か月を超えています。",
    });
  }

  return true;
};

export const learnerPlanSchema = validator.object().shape({
  planStatus: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("受験ステータス").notRequired(),
  curriculumProgressStatus: validator
    .string()
    .trim()
    .max(MAX_CHARACTER_INPUT_VALIDATION)
    .emptyToNull()
    .nullable()
    .label("カリキュラム進捗ステータス")
    .notRequired(),
  // Required only when the learner uses the learning app; cleared & optional when isUsingLearningApp is OFF
  learningStartDate: validator
    .mixed()
    .isDayjs()
    .isSelection()
    .label("カリキュラム開始日")
    .nullable()
    .notRequired()
    .test("requiredWhenUsingLearningApp", "", function (value) {
      const root = this.from?.[this.from.length - 1]?.value as { isUsingLearningApp?: boolean | null } | undefined;
      if (root?.isUsingLearningApp === false) return true;
      if (value) return true;
      return this.createError({ path: this.path, message: replaceStr(INVALID_SELECT_NOT_EMPTY, { path: "カリキュラム開始日" }) });
    }),
  examScheduledYearMonth: validator.mixed().isDayjs().isSelection().label("受験予定年月").required(),
  isSelfStudyMode: validator.boolean().nullable().label("自己学習モード").notRequired(),
  partTypes: validator
    .array()
    .of(validator.string().trim().emptyToNull().required())
    .min(1, "「受験・学習対象のパート」を選択してください。")
    .nullable()
    .label("受験・学習対象のパート")
    .notRequired(),
  examId: validator.string().isSelection().trim().emptyToNull().label("受験予定").required(),
});

const baseLearnerFields = {
  fullName: validator.string().trim().max(MAX_CHARACTER_FULLNAME_VALIDATION).emptyToNull().label("氏名").required(),
  nickName: validator.string().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("呼び名").notRequired(),
  nationalityId: validator.string().isSelection().trim().emptyToNull().label("国籍").required(),
  gender: validator.string().isSelection().trim().emptyToNull().label("性別").required(),
  prefecture: validator
    .string()
    .isSelection()
    .trim()
    .max(MAX_CHARACTER_INPUT_VALIDATION)
    .emptyToNull()
    .nullable()
    .label("居住地　都道府県")
    .notRequired(),
  city: validator.string().isSelection().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("市区町村").notRequired(),

  clause1StartDate: validator
    .mixed()
    .isDayjs()
    .isSelection()
    .isPastOrToday()
    .label("1号の起算日")
    .nullable()
    .notRequired()
    .when("blankPeriodMonths", ([blankPeriodMonths], schema) => {
      return schema.test("checkRemainingTermOnStartDate", "", function (value) {
        return checkRemainingTerm(value, blankPeriodMonths, this.path, this.createError.bind(this));
      });
    }),
  blankPeriodMonths: validator
    .number()
    .commas()
    .autoTransformToHalfWidth()
    .integer()
    .min(0)
    .max(600)
    .emptyToNull()
    .nullable()
    .label("ブランク期間（月単位）")
    .notRequired()
    .when("clause1StartDate", ([clause1StartDate], schema) => {
      return schema.test("checkRemainingTermOnBlankPeriod", "", function (value) {
        return checkRemainingTerm(clause1StartDate, value, this.path, this.createError.bind(this));
      });
    }),

  industryId: validator.string().isSelection().trim().emptyToNull().label("受験分野").required(),
  japaneseLanguageLevel: validator.string().isSelection().trim().max(MAX_CHARACTER_INPUT_VALIDATION).emptyToNull().nullable().label("日本語能力").notRequired(),
  lastExamDate: validator.mixed().isDayjs().nullable().label("前回試験の受験日").notRequired(),
  // Score cap of 1000 is the product rule shared with the learning-progress filter
  // (see MAX_EXAM_SCORE in learningProgressFilterSchema.ts). Apply to both create &
  // edit learner forms so existing records cannot exceed the same hard limit.
  lastExamScore: validator
    .number()
    .commas()
    .autoTransformToHalfWidth()
    .min(0)
    .integer()
    .max(MAX_EXAM_SCORE)
    .emptyToNull()
    .nullable()
    .label("前回試験の点数")
    .notRequired(),

  memo: validator.string().trim().max(MAX_TEXT_INPUT_VALIDATION).emptyToNull().nullable().label("メモ").notRequired(),

  learnerPlan: learnerPlanSchema,
};

// Declare cyclic dependency between clause1StartDate and blankPeriodMonths
const cyclicDeps: [string, string][] = [["clause1StartDate", "blankPeriodMonths"]];

export const learnerSchema = validator.object().shape(baseLearnerFields, cyclicDeps);

// Schema with companyId required (for RSO users)
export const learnerSchemaWithCompany = validator.object().shape(
  {
    ...baseLearnerFields,
    rsoCompanyId: validator.string().isSelection().trim().emptyToNull().label("所属機関").required(),
  },
  cyclicDeps,
);
